/**
 * Error Boundary
 *
 * React class component that catches render errors in its subtree.
 * React hooks cannot catch render errors — only class component
 * componentDidCatch / getDerivedStateFromError can. This is a React
 * limitation, not a design choice.
 *
 * The retryKey pattern forces a full remount on retry — prevents the
 * re-crash loop that happens when setState({hasError: false}) replays
 * the same failing render with unchanged data.
 *
 * Usage:
 *   <ErrorBoundary fallback={<WidgetErrorFallback />}>
 *     <SomeWidget />
 *   </ErrorBoundary>
 *
 *   // With reset callback:
 *   <ErrorBoundary fallback={(reset) => <WidgetErrorFallback onRetry={reset} />}>
 *     <SomeWidget />
 *   </ErrorBoundary>
 */

import { Component, Fragment, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Static fallback, or render function that receives a reset callback */
  fallback?: ReactNode | ((reset: () => void) => ReactNode);
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** Incrementing key forces React to remount children on retry */
  retryKey: number;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary] Caught render error:", error, info);
    }
  }

  private handleReset = (): void => {
    this.setState((prev) => ({
      hasError: false,
      retryKey: prev.retryKey + 1,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;

      // Render function form: pass reset callback so fallback can trigger retry
      if (typeof fallback === "function") {
        return fallback(this.handleReset);
      }

      // Static fallback provided
      if (fallback) {
        return fallback;
      }

      // Default fallback
      return (
        <div className="widget">
          <div className="widget-body py-6 text-center">
            <p className="text-sm text-accent-red">Something went wrong</p>
            <button
              onClick={this.handleReset}
              className="mt-2 text-xs text-text-muted hover:text-text-primary transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    // Key change forces full remount — prevents re-crash from stale error state
    return <Fragment key={this.state.retryKey}>{this.props.children}</Fragment>;
  }
}
